import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { RouteNames } from 'app/Routes'
import { PROFILE_URL } from 'app/stlswing/content/classes'
import logo from 'assets/images/logo-dark.png'
import logoWhite from 'assets/images/logo-white.png'

type NavRoute = { text: string; route: RouteNames }

const classRoutes: NavRoute[] = [
  { text: 'Starting Swing', route: '/starting-swing' },
  { text: 'Wednesday Night Swing', route: '/wednesday-night-swing' },
  { text: 'Curriculum', route: '/curriculum' },
  { text: 'Upcoming Classes', route: '/swing-dance-classes/groupClasses' },
]

const routes: NavRoute[] = [
  { text: 'Events', route: '/swing-dance-events' },
  { text: 'About', route: '/about' },
  { text: 'FAQ', route: '/faq' },
]

export const isHomeRoute = (pathname: string) =>
  pathname === '/' || pathname === '/home'

/** On the home page the nav is see-through over the hero until you start scrolling */
const useTransparentOverHero = (pathname: string) => {
  const [overHero, setOverHero] = useState(isHomeRoute(pathname))

  useEffect(() => {
    if (!isHomeRoute(pathname)) return setOverHero(false)

    const onScroll = () => setOverHero(window.scrollY < 40)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [pathname])

  return overHero
}

// Padding gives the links a comfortable target without changing how they look
const linkClasses = (active: boolean, onDark = false) => {
  const base = 'px-3 py-3 text-[15px] font-bold rounded-full'
  if (onDark) return `${base} text-white hover:text-spot`
  return `${base} hover:text-iris ${active ? 'text-iris' : 'text-ink'}`
}

const ClassesDropdown = ({
  pathname,
  onDark,
}: {
  pathname: string
  onDark: boolean
}) => {
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const active = classRoutes.some(({ route }) => pathname.startsWith(route))

  useEffect(() => setOpen(false), [pathname])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!container.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={container} className='relative'>
      <button
        aria-expanded={open}
        aria-controls='classes-menu'
        onClick={() => setOpen((isOpen) => !isOpen)}
        className={`flex items-center ${linkClasses(active, onDark)}`}>
        Classes
        <svg
          className={`w-4 h-4 ml-1 transition-transform duration-200 motion-reduce:transition-none ${
            open ? 'rotate-180' : ''
          }`}
          viewBox='0 0 20 20'
          fill='currentColor'
          aria-hidden='true'>
          <path d='M5.3 7.3a1 1 0 011.4 0L10 10.6l3.3-3.3a1 1 0 111.4 1.4l-4 4a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4z' />
        </svg>
      </button>

      <div
        id='classes-menu'
        className={`absolute left-0 w-72 p-2 mt-2 text-ink bg-white border border-rule rounded-3xl shadow-xl transition-all duration-200 motion-reduce:transition-none origin-top ${
          open
            ? 'visible opacity-100 translate-y-0'
            : 'invisible opacity-0 -translate-y-2'
        }`}>
        {classRoutes.map(({ text, route }) => (
          <Link
            key={route}
            to={route}
            aria-current={pathname.startsWith(route) ? 'page' : undefined}
            className={`block px-4 py-3 font-bold rounded-2xl hover:bg-paper ${
              pathname.startsWith(route) ? 'text-iris' : ''
            }`}>
            {text}
          </Link>
        ))}
      </div>
    </div>
  )
}

// The mobile menu groups links differently from the desktop bar
const menuGroups: { heading: string; links: NavRoute[] }[] = [
  {
    heading: 'Events',
    links: [
      { text: 'Starting Swing', route: '/starting-swing' },
      { text: 'Wednesday Night Swing', route: '/wednesday-night-swing' },
      { text: 'Upcoming Events', route: '/swing-dance-classes/groupClasses' },
    ],
  },
  {
    heading: 'More',
    links: [{ text: 'Curriculum', route: '/curriculum' }, ...routes],
  },
]

const motion = 'duration-300 ease-out motion-reduce:transition-none'

/** Two bars that cross into an X, with the word beside them swapping to match */
const MenuButton = ({
  open,
  onClick,
}: {
  open: boolean
  onClick: () => void
}) => {
  const bar = `absolute left-0 w-5 h-0.5 bg-current transition-transform ${motion}`
  const word = `absolute inset-0 flex items-center transition-all ${motion}`

  return (
    <button
      className='flex items-center h-11 pl-3 -mr-1 font-bold'
      aria-expanded={open}
      aria-controls='site-menu'
      aria-label={open ? 'Close menu' : 'Open menu'}
      data-testid='open-mobile-header-button'
      onClick={onClick}>
      <span
        className='relative w-12 h-6 mr-1 overflow-hidden'
        aria-hidden='true'>
        <span
          className={`${word} ${
            open ? '-translate-y-full opacity-0' : 'translate-y-0 opacity-100'
          }`}>
          Menu
        </span>
        <span
          className={`${word} ${
            open ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
          }`}>
          Close
        </span>
      </span>
      <span className='relative w-5 h-4' aria-hidden='true'>
        <span
          className={`${bar} ${
            open ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-1 rotate-0'
          }`}
        />
        <span
          className={`${bar} ${
            open ? 'top-1/2 -translate-y-1/2 -rotate-45' : 'bottom-1 rotate-0'
          }`}
        />
      </span>
    </button>
  )
}

/**
 * Fixed top bar. Below lg the links move into a full screen menu that slides
 * down from under the bar, and the bar turns dark to join it.
 */
export const SiteNav = () => {
  const { pathname } = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const overHero = useTransparentOverHero(pathname)
  // The bar has light text over the home hero and while the menu is open
  const onDark = overHero || menuOpen

  useEffect(() => {
    setMenuOpen(false)
  }, [pathname])

  // The page behind the menu shouldn't scroll
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuOpen])

  const barColors = menuOpen
    ? 'bg-ink border-transparent text-white'
    : overHero
      ? 'bg-transparent border-transparent text-white'
      : 'bg-paper/90 backdrop-blur border-rule text-ink'

  const menuLink =
    'block py-3 text-4xl leading-none uppercase font-display font-black'

  return (
    <nav
      className={`fixed inset-x-0 top-0 z-40 border-b transition-colors ${motion} ${barColors}`}>
      <div className='flex items-center justify-between h-16 max-w-6xl px-4 mx-auto sm:px-6'>
        <Link to='/' className='flex items-center space-x-2'>
          {/* Both logos are stacked so they can cross fade */}
          <span className='relative w-10 h-10'>
            <img
              src={logo}
              alt=''
              className={`absolute inset-0 w-10 h-10 transition-opacity ${motion} ${
                onDark ? 'opacity-0' : 'opacity-100'
              }`}
            />
            <img
              src={logoWhite}
              alt=''
              className={`absolute inset-0 w-10 h-10 transition-opacity ${motion} ${
                onDark ? 'opacity-100' : 'opacity-0'
              }`}
            />
          </span>
          <span className='text-2xl leading-none uppercase font-display font-black'>
            STL Swing
          </span>
        </Link>

        <div className='items-center hidden space-x-2 lg:flex'>
          <ClassesDropdown pathname={pathname} onDark={onDark} />
          {routes.map(({ text, route }) => {
            const active = pathname.startsWith(route)
            return (
              <Link
                key={route}
                to={route}
                aria-current={active ? 'page' : undefined}
                className={linkClasses(active, onDark)}>
                {text}
              </Link>
            )
          })}
          <a
            href={PROFILE_URL}
            className={`!ml-4 btn btn-sm ${
              onDark ? 'btn-outline-light' : 'btn-outline'
            }`}>
            Login
          </a>
        </div>

        {/* Below lg: Login only appears while the menu is open, the links go in the menu */}
        <div className='flex items-center space-x-1 lg:hidden'>
          <a
            href={PROFILE_URL}
            tabIndex={menuOpen ? 0 : -1}
            aria-hidden={!menuOpen}
            className={`btn btn-sm btn-outline-light transition-all ${motion} ${
              menuOpen
                ? 'visible opacity-100 translate-x-0'
                : 'invisible opacity-0 translate-x-2'
            }`}>
            Login
          </a>
          <MenuButton
            open={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          />
        </div>
      </div>

      {/* Always in the page so it can animate both ways; hidden from clicks and screen readers when closed */}
      <div
        id='site-menu'
        aria-hidden={!menuOpen}
        className={`absolute inset-x-0 top-16 h-[calc(100vh-4rem)] px-4 pb-12 overflow-y-auto text-white bg-night-glow transition-all ${motion} lg:hidden ${
          menuOpen
            ? 'visible opacity-100 translate-y-0'
            : 'invisible opacity-0 -translate-y-3'
        }`}>
        {menuGroups.map((group, groupIndex) => (
          <div
            key={group.heading}
            style={{
              transitionDelay: menuOpen ? `${80 + groupIndex * 70}ms` : '0ms',
            }}
            className={`transition-all ${motion} ${
              menuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
            } ${groupIndex > 0 ? 'mt-4 border-t border-white/20' : ''}`}>
            <p className='pt-6 kicker !text-spot'>{group.heading}</p>
            {group.links.map(({ text, route }) => (
              <Link
                key={route}
                to={route}
                tabIndex={menuOpen ? 0 : -1}
                className={menuLink}>
                {text}
              </Link>
            ))}
          </div>
        ))}
      </div>
    </nav>
  )
}
